import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  IconButton,
  Skeleton,
  Button,
  Slider,
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fetchRecentFromAPI } from "../../../data/Data";
import { baseUrlWithoutApi } from "../../../data/BaseUrl";
import PropertyCard from "./PropertyCard";

const PropertyFilter = () => {
  const lang = localStorage.getItem("language") || "arabic";
  const isArabic = lang === "arabic";
  const [cities, setCities] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedCity, setSelectedCity] = useState(
    () => JSON.parse(localStorage.getItem("selectedCity")) || null
  );
  const [selectedNeighborhood, setSelectedNeighborhood] = useState(
    () => JSON.parse(localStorage.getItem("selectedNeighborhood")) || null
  );
  const [priceRange, setPriceRange] = useState([0, 7000000]);
  const [allProperties, setAllProperties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const pageSize = 6;
  const [types, setTypes] = useState([]);
  const [selectedType, setSelectedType] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchRecentFromAPI()
      .then((data) => {
        setAllProperties(data);

        const cityList = Array.from(
          new Map(
            data
              .map((item) => {
                const ar = item.location?.split(" - ")[1]?.trim();
                const en = item.location_en?.split(" - ")[1]?.trim();
                return ar ? [ar, { ar, en: en || ar }] : null;
              })
              .filter(Boolean)
          ).values()
        );
        setCities(cityList);
        // استرجاع المحفوظات
        const savedCity = JSON.parse(localStorage.getItem("selectedCity"));
        const matchedCity = cityList.find((c) => c.ar === savedCity?.ar);
        if (matchedCity) {
          setSelectedCity(matchedCity);
          localStorage.setItem("selectedCity", JSON.stringify(matchedCity));
        }
      })
      .catch((err) => console.error("Error fetching properties:", err))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (selectedCity) {
      setNeighborhoods([]);
      setSelectedNeighborhood(null);
      setPage(0);
      localStorage.setItem("selectedCity", JSON.stringify(selectedCity));

      const hoods = allProperties
        .filter((item) => item.location?.includes(`- ${selectedCity.ar}`))
        .map((item) => {
          const arabic = item.location?.split(" - ")[0].trim();
          const english = item.location_en?.split(" - ")[0].trim();
          return { ar: arabic, en: english || arabic };
        });

      const uniqueHoods = Array.from(
        new Map(hoods.map((h) => [h.ar, h])).values()
      );
      setNeighborhoods(uniqueHoods);

      // Update types based on city only
      const cityFiltered = allProperties.filter((item) =>
        item.location?.includes(`- ${selectedCity.ar}`)
      );

      const updatedTypes = Array.from(
        new Map(
          cityFiltered
            .map((item) => {
              const ar = item.type?.trim();
              const en = item.type_en?.trim();
              return ar ? [ar, { ar, en: en || ar }] : null;
            })
            .filter(Boolean)
        ).values()
      );
      setTypes(updatedTypes);
    } else {
      localStorage.removeItem("selectedCity");
    }
  }, [selectedCity, allProperties]);

  useEffect(() => {
    if (selectedNeighborhood) {
      localStorage.setItem(
        "selectedNeighborhood",
        JSON.stringify(selectedNeighborhood)
      );

      const filtered = allProperties.filter(
        (item) =>
          item.location?.includes(`- ${selectedCity?.ar}`) &&
          item.location?.startsWith(selectedNeighborhood.ar)
      );

      const typesByNeighborhood = Array.from(
        new Map(
          filtered
            .map((item) => {
              const ar = item.type?.trim();
              const en = item.type_en?.trim();
              return ar ? [ar, { ar, en: en || ar }] : null;
            })
            .filter(Boolean)
        ).values()
      );
      setTypes(typesByNeighborhood);
    } else {
      localStorage.removeItem("selectedNeighborhood");
    }
  }, [selectedNeighborhood]);

  const extractPriceValue = (priceStr) => {
    const match =
      typeof priceStr === "string" ? priceStr.replace(/[^\d]/g, "") : "0";
    return parseInt(match || "0", 10);
  };

  const filteredProperties = useMemo(() => {
    let filtered = allProperties;
    if (selectedCity) {
      filtered = filtered.filter((prop) =>
        prop.location?.includes(`- ${selectedCity.ar}`)
      );
    }
    if (selectedNeighborhood) {
      filtered = filtered.filter((prop) =>
        prop.location?.startsWith(selectedNeighborhood.ar)
      );
    }
    if (selectedType) {
      filtered = filtered.filter((prop) => prop.type === selectedType.ar);
    }
    filtered = filtered.filter((prop) => {
      const price = extractPriceValue(prop.price);
      return price >= priceRange[0] && price <= priceRange[1];
    });
    return filtered;
  }, [
    allProperties,
    selectedCity,
    selectedNeighborhood,
    selectedType,
    priceRange,
  ]);

  const paginatedProperties = useMemo(() => {
    const start = page * pageSize;
    return filteredProperties.slice(start, start + pageSize);
  }, [filteredProperties, page]);

  const pageCount = Math.ceil(filteredProperties.length / pageSize);

  const resetFilters = () => {
    setSelectedCity(null);
    setSelectedNeighborhood(null);
    setSelectedType(null);
    setPriceRange([0, 7000000]);
    setPage(0);
    localStorage.removeItem("selectedCity");
    localStorage.removeItem("selectedNeighborhood");

    const allTypes = Array.from(
      new Map(
        allProperties
          .map((item) => {
            const ar = item.type?.trim();
            const en = item.type_en?.trim();
            return ar ? [ar, { ar, en: en || ar }] : null;
          })
          .filter(Boolean)
      ).values()
    );
    setTypes(allTypes);
  };

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        maxWidth: 1200,
        mx: "auto",
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      <Box textAlign="center" mb={4}>
        <Typography
          variant="overline"
          color="primary"
          sx={{ fontFamily: '"Alexandria", sans-serif' }}
        >
          {isArabic ? "تصفح" : "Browse"}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            mt: 1,
            fontFamily: '"Alexandria", sans-serif',
            fontWeight: 700,
          }}
        >
          {isArabic
            ? "اعثر على عقارك حسب المدينة والحي"
            : "Find your property by city and neighborhood"}
        </Typography>
        <Box
          sx={{
            width: 32,
            height: 4,
            bgcolor: "primary.main",
            mx: "auto",
            mt: 1,
            mb: 2,
          }}
        />
      </Box>

      <Grid container spacing={2} mb={2}>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>{isArabic ? "المدينة" : "City"}</InputLabel>
            <Select
              value={selectedCity}
              onChange={(e) => {
                setSelectedCity(e.target.value);
                localStorage.setItem(
                  "selectedCity",
                  JSON.stringify(e.target.value)
                );
                setPage(0);
                setSelectedType(null);
              }}
              label={isArabic ? "المدينة" : "City"}
            >
              {cities.map((city, i) => (
                <MenuItem key={i} value={city}>
                  {isArabic ? city.ar : city.en}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormControl fullWidth disabled={!selectedCity}>
            <InputLabel>{isArabic ? "الحي" : "Neighborhood"}</InputLabel>
            <Select
              value={selectedNeighborhood || ""}
              onChange={(e) => {
                setSelectedNeighborhood(e.target.value);
                setPage(0);
                setSelectedType(null);
              }}
              label={isArabic ? "الحي" : "Neighborhood"}
            >
              {neighborhoods.map((hood, i) => (
                <MenuItem key={i} value={hood}>
                  {isArabic ? hood.ar : hood.en}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>{isArabic ? "نوع العقار" : "Property Type"}</InputLabel>
            <Select
              value={selectedType || ""}
              onChange={(e) => {
                setSelectedType(e.target.value);
                setPage(0);
              }}
              label={isArabic ? "نوع العقار" : "Property Type"}
            >
              {types.map((type, i) => (
                <MenuItem key={i} value={type}>
                  {isArabic ? type.ar : type.en}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ px: { xs: 1, md: 0 }, mt: { xs: 2, md: 0 } }}>
            <Typography gutterBottom>
              {isArabic ? "نطاق السعر (ريال)" : "Price Range (SAR)"}
            </Typography>
            <Slider
              value={priceRange || ""}
              onChange={(e, newValue) => setPriceRange(newValue)}
              valueLabelDisplay="auto"
              step={5000}
              min={0}
              max={7000000}
            />
          </Box>
        </Grid>
      </Grid>

      <Box textAlign={isArabic ? "left" : "right"} mb={2}>
        <Button onClick={resetFilters}>
          {isArabic ? "إعادة تعيين الفلاتر" : "Reset Filters"}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ px: { xs: 1, md: 0 } }}>
          <Grid container spacing={{ xs: 2, md: 3 }}>
            {[...Array(3)].map((_, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <Skeleton variant="rounded" height={300} />
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : paginatedProperties.length ? (
        <>
          <Box sx={{ px: { xs: 1, md: 0 } }}>
            <Grid container spacing={{ xs: 2, md: 3 }}>
              {paginatedProperties.map((prop) => (
                <Grid item xs={12} sm={6} md={4} key={prop.id || prop._id}>
                  <PropertyCard
                    property={{
                      _id: prop.id,
                      image: `${baseUrlWithoutApi}${prop.cover}`,
                      beds: prop.bedrooms,
                      baths: prop.bathrooms,
                      sqft: prop.property_area,
                      featured: prop.license_number?.includes("#"),
                      arabic: {
                        title: prop.name,
                        location: prop.location,
                        price: prop.price,
                        type: prop.type,
                        availability: prop.category,
                      },
                      english: {
                        title: prop.name_en,
                        location: prop.location_en,
                        price: prop.price_en,
                        type: prop.type_en,
                        availability: prop.category_en,
                      },
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {pageCount > 1 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              mt={4}
              gap={2}
            >
              <IconButton
                disabled={page === 0}
                onClick={() => setPage((p) => Math.max(p - 1, 0))}
              >
                {isArabic ? <ChevronRight /> : <ChevronLeft />}
              </IconButton>
              <Typography>
                {page + 1} / {pageCount}
              </Typography>
              <IconButton
                disabled={page === pageCount - 1}
                onClick={() => setPage((p) => Math.min(p + 1, pageCount - 1))}
              >
                {isArabic ? <ChevronLeft /> : <ChevronRight />}
              </IconButton>
            </Box>
          )}
        </>
      ) : (
        <Box textAlign="center" py={5}>
          <Typography
            variant="subtitle1"
            sx={{ fontFamily: '"Alexandria", sans-serif' }}
          >
            {isArabic ? "لا توجد نتائج لعرضها." : "No results to display."}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default PropertyFilter;
